/**
 * CSAT custom sending domains — staff setup section, rendered by the
 * "CSAT Custom Domain" tab on the company detail page (SF-4343 → SF-4373 →
 * SF-4350, epic SF-4337; Technical Guide §6.1).
 *
 * Staff run the whole onboarding for the chargeable add-on from here: the
 * on/off toggle and the company-default choice live in CsatDomainTab (saved
 * immediately via the company PATCH); this section renders when the SAVED
 * flag is on and owns everything else. A company may hold SEVERAL domains
 * (SF-4350): a table lists them all (default first) with Make default /
 * Remove per row; selecting a row shows its DNS pack with copy buttons,
 * per-record status, re-check, test-send and the sender editor; "Add another
 * domain" opens the create form. Every per-domain action carries `?domainId=`
 * so it never silently acts on the default. Test-send requires the saved flag
 * (sf-api picks the sender from the persisted flag) and reports the API's
 * actual result (recipient used, sender, sent or not).
 *
 * Visibility is gated on `admin:companies:domain:read`, mutating controls on
 * `admin:companies:domain:write`. Server-side enforcement is authoritative;
 * this gating is defence in depth.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';

import { hasPermission } from '../../../../utils/permissions';
import { useCanEditCompany } from '../../api/editableCompaniesClient';
import {
  CSAT_DOMAIN_READ_PERMISSION,
  CSAT_DOMAIN_WRITE_PERMISSION,
  canTestSend,
  canTestSendNow,
  isHostingLive,
  halfErrorReason,
  createSendingDomainArgs,
  displayValue,
  dnsPackAsText,
  dnsPackAsHtml,
  DNS_PURPOSE_LABEL,
  describeTestSend,
  parseTestSendResult,
  recheckSendingDomainArgs,
  removeSendingDomainArgs,
  setDefaultSendingDomainArgs,
  updateSenderArgs,
  splitMailbox,
  isValidLocalPart,
  isValidMailbox,
  normalizeDomainInput,
  isValidSendingHostname,
  looksLikeRootDomain,
  type UpdateSenderBody,
  sendingDomainErrorMessage,
  testSendArgs,
  useSendingDomains,
  useSendingDomainMutation,
  type CreateDomainFormValues,
  createBodyFromFormValues,
  type DnsRecord,
  type SendingDomain,
  type TestSendOutcome,
} from '../../api/csatDomainClient';

const { Text, Paragraph } = Typography;

export interface CsatCustomDomainSectionProps {
  /** Company ObjectId. */
  companyId: string;
  /** Whether the setup section is shown. Since SF-4373 the only caller (CsatDomainTab) passes the SAVED flag. */
  enabled: boolean;
  /**
   * Saved value of `csat_custom_domain_enabled`. Test-send is gated on THIS:
   * sf-api resolves the sender from the persisted flag. Kept as a separate
   * prop so the gate stays explicit; CsatDomainTab passes `enabled` here too.
   */
  savedEnabled: boolean;
}

const PURPOSE_LABEL = DNS_PURPOSE_LABEL;

function RecordStatusPill({ valid }: { valid: boolean | null }) {
  if (valid === true) return <Tag color="success">ok</Tag>;
  if (valid === false) return <Tag color="error">wrong value</Tag>;
  return <Tag>pending</Tag>;
}

/**
 * Long provider messages (e.g. a full CloudFront/SendGrid error) must not be
 * jammed into a Tag — antd Tags are `white-space: nowrap`, so the text runs
 * off the edge of the page. Anything longer than this is shown as a short
 * "failed" pill with the full reason in a wrapping line beneath.
 */
const PILL_REASON_MAX_CHARS = 60;

function overallStatus(record: SendingDomain): { text: string; color: string } {
  switch (record.status) {
    case 'live':
      return { text: 'Live — email and survey hosting verified', color: 'success' };
    case 'email_ready':
      return { text: 'Email ready — survey hosting still waiting for DNS', color: 'processing' };
    case 'web_ready':
      return { text: 'Survey hosting live — email still waiting for DNS', color: 'processing' };
    case 'error': {
      // Same nowrap-Tag rule as the half pills: a long provider message goes
      // to the wrapping detail line, not into the Tag.
      const reason = record.error_reason ?? 'unknown';
      return {
        text:
          reason.length > PILL_REASON_MAX_CHARS ? 'Error — see details below' : `Error: ${reason}`,
        color: 'error',
      };
    }
    default: {
      const since = record.dns_wait_started_at
        ? new Date(record.dns_wait_started_at).toLocaleDateString()
        : 'now';
      return { text: `Blocked: waiting for DNS since ${since}`, color: 'warning' };
    }
  }
}

/** One pill per independent half: email sending, survey hosting. */
function HalfPill({
  label,
  valid,
  errorReason,
}: {
  label: string;
  valid: boolean | undefined;
  errorReason?: string | null;
}) {
  if (valid)
    return (
      <Tag color="success" style={{ marginInlineEnd: 0 }}>
        {label}: verified
      </Tag>
    );
  if (errorReason) {
    const short =
      errorReason === 'dns_timeout'
        ? 'gave up waiting for DNS (re-check once records are added)'
        : errorReason.length > PILL_REASON_MAX_CHARS
          ? 'failed (see below)'
          : errorReason;
    return (
      <Tag color="error" style={{ marginInlineEnd: 0 }}>
        {label}: {short}
      </Tag>
    );
  }
  return (
    <Tag color="warning" style={{ marginInlineEnd: 0 }}>
      {label}: waiting for DNS
    </Tag>
  );
}

/** Full-length reasons that were too long for a pill, one wrapping line each. */
function HalfErrorDetails({ items }: { items: { label: string; reason: string }[] }) {
  const long = items.filter(
    (i) => i.reason !== 'dns_timeout' && i.reason.length > PILL_REASON_MAX_CHARS,
  );
  if (long.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }} data-testid="csat-status-details">
      {long.map((i) => (
        <Text
          key={i.label}
          type="danger"
          style={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}
          data-testid={`csat-half-error-${i.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {i.label}: {i.reason}
        </Text>
      ))}
    </div>
  );
}

/**
 * The Status cell. antd's Descriptions.Item content is an inline-flex, nowrap
 * box, so anything placed directly inside it becomes a sibling COLUMN next to
 * the Tags rather than a row beneath them (a long reason rendered as a
 * 14px-wide, 3000px-tall strip). One block-level wrapper that takes the full
 * cell width, a wrapping row for the pills, and the details as a block below.
 */
function StatusCell({ record }: { record: SendingDomain }) {
  const overall = overallStatus(record);
  const overallReason = record.status === 'error' ? (record.error_reason ?? null) : null;
  const emailReason = halfErrorReason(record, 'email');
  const webReason = halfErrorReason(record, 'web');
  return (
    <div style={{ display: 'block', width: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        <Tag
          color={overall.color}
          data-testid="csat-domain-overall-status"
          style={{ marginInlineEnd: 0 }}
        >
          {overall.text}
        </Tag>
        {/* Both props take the same fallbacks as the action gates: legacy
            rows carry only `status`, and a wholesale failure carries
            error_reason on the RECORD, not the half — reading the half
            alone showed "waiting for DNS" beside a verified or error
            overall line (Bugbot: half pills ignore status fallback /
            error status pills ignore fallback). */}
        <HalfPill label="Email" valid={canTestSend(record)} errorReason={emailReason} />
        <HalfPill label="Survey hosting" valid={isHostingLive(record)} errorReason={webReason} />
      </div>
      <HalfErrorDetails
        items={[
          // A record-level reason is already the halves' fallback; list it once.
          ...(overallReason && overallReason !== emailReason && overallReason !== webReason
            ? [{ label: 'Error', reason: overallReason }]
            : []),
          { label: 'Email', reason: emailReason ?? '' },
          { label: 'Survey hosting', reason: webReason ?? '' },
        ]}
      />
    </div>
  );
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    message.success(`${label} copied`);
  } catch {
    message.error('Could not copy to clipboard');
  }
}

/**
 * Copy text AND an HTML rendering in one clipboard item, so pasting into a
 * mail client yields a real table while plain-text targets get `text`.
 * Falls back to text-only where ClipboardItem is unavailable (Firefox < 127,
 * insecure contexts, jsdom).
 */
async function copyRichToClipboard(text: string, html: string, label: string) {
  const canRich =
    typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';
  if (!canRich) return copyToClipboard(text, label);
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([text], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      }),
    ]);
    message.success(`${label} copied`);
  } catch {
    // Some browsers reject text/html even with ClipboardItem present.
    await copyToClipboard(text, label);
  }
}

export const CsatCustomDomainSection: React.FC<CsatCustomDomainSectionProps> = ({
  companyId,
  enabled,
  savedEnabled,
}) => {
  const { data: permissions } = usePermissions<string[]>();
  const canRead = hasPermission(permissions ?? [], CSAT_DOMAIN_READ_PERMISSION);
  // SF-4668: staff with a company list may only change the companies on it.
  const { canEditCompany } = useCanEditCompany(companyId);
  const canWrite = hasPermission(permissions ?? [], CSAT_DOMAIN_WRITE_PERMISSION) && canEditCompany;

  // Fetch whenever records COULD exist: the toggle is on now, or was saved
  // on (turning it off keeps the records so it can be re-enabled — AC3 of SF-4338).
  const query = useSendingDomains(companyId, { enabled: canRead && (enabled || savedEnabled) });
  const domains: SendingDomain[] = useMemo(
    () => (query.isError ? [] : (query.data?.data ?? [])),
    [query.data, query.isError],
  );
  // Which domain's detail panel is open. null = none chosen; the default (or
  // the only domain) is shown once the list lands. Keyed by id so a background
  // refetch that reorders rows cannot swap which record the DNS pack /
  // test-send / sender editor refer to.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Explicit "Add another domain" mode when domains already exist. With none
  // at all the create form shows by itself.
  const [adding, setAdding] = useState(false);
  const record: SendingDomain | undefined = useMemo(() => {
    if (domains.length === 0) return undefined;
    const bySelection = selectedId ? domains.find((d) => d._id === selectedId) : undefined;
    return bySelection ?? domains.find((d) => d.is_default) ?? domains[0];
  }, [domains, selectedId]);
  const noDomains = !query.isLoading && !query.isError && domains.length === 0;
  const showCreateForm = noDomains || adding;

  const mutation = useSendingDomainMutation();
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    'create' | 'recheck' | 'test' | 'remove' | 'sender' | 'default' | null
  >(null);
  // The row a row-level action (Make default / Remove) targets, so only that
  // row's button shows the spinner while every row's controls are disabled.
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  // Inline "Edit sender" on the existing-record view (from local part / from
  // name / reply-to). The domain is immutable — rendered as a locked suffix.
  const [editingSender, setEditingSender] = useState(false);
  // The domain id the editor is bound to. Save/Cancel act on THIS id, not
  // whatever `record._id` happens to be at render time — otherwise a
  // background refetch that swaps in a different domain (e.g. another staff
  // session changed the default) would silently save the open draft onto the
  // new record (Bugbot).
  const [editingDomainId, setEditingDomainId] = useState<string | null>(null);
  const [senderForm] = Form.useForm<{ from_local: string; from_name: string; reply_to: string }>();
  const [testTo, setTestTo] = useState('');
  // Outcome of the last test-send for THIS company, taken from the response.
  // Tagged with the domain it was sent for and rendered only while THAT domain
  // is the displayed record — so however the panel moves (row click, remove,
  // default change, background refetch) a result never shows under another
  // domain (Bugbot).
  const [testOutcomeFor, setTestOutcomeFor] = useState<{
    domainId: string;
    outcome: TestSendOutcome;
  } | null>(null);
  const testOutcome =
    record?._id && testOutcomeFor?.domainId === record._id ? testOutcomeFor.outcome : null;
  const { token } = theme.useToken();
  const [form] = Form.useForm<CreateDomainFormValues>();
  // Pinned suffix on the create form's From address. Watched rather than read
  // via getFieldValue so the suffix re-renders as the subdomain is typed.
  const createDomainSuffix = normalizeDomainInput(Form.useWatch('domain', form));
  // The company the currently in-flight mutation was started for. A mutation
  // that lands after staff switched companies must not apply its result to the
  // new company (its onSuccess would set removedLocally / refetch / message).
  const inFlightCompanyRef = useRef<string | null>(null);

  useEffect(() => {
    // Per-company transient state must not leak into the next company's page.
    setActionError(null);
    setSelectedId(null);
    setAdding(false);
    setBusy(null);
    setBusyRowId(null);
    setTestTo('');
    setTestOutcomeFor(null);
    setEditingSender(false);
    setEditingDomainId(null);
    senderForm.resetFields();
    // Abandon any in-flight mutation's callbacks — they belong to the company
    // we just navigated away from.
    inFlightCompanyRef.current = null;
    // The Form.useForm instance outlives a company switch on the cached edit
    // page — clear typed values so company A's subdomain/From/Reply-To are
    // never submitted for company B.
    form.resetFields();
  }, [companyId, form, senderForm]);

  const dnsText = useMemo(() => (record ? dnsPackAsText(record.dns_pack ?? []) : ''), [record]);
  const dnsHtml = useMemo(() => (record ? dnsPackAsHtml(record.dns_pack ?? []) : ''), [record]);
  // The "_dmarc" hint only makes sense when a DMARC row is actually offered —
  // it is suppressed when the parent zone already has a policy (SF-4340).
  const hasDmarcRow = (record?.dns_pack ?? []).some((r) => r.purpose === 'dmarc');

  // Same identity guard as onRemove/onRecheck would need: if the record
  // rendering now is not the one the editor was opened for (a refetch swapped
  // in a different domain while the form was open), close without saving
  // rather than PATCHing the wrong record with stale values.
  const editingRecordMismatched = editingSender && record?._id !== editingDomainId;

  // …and do that the moment it happens, not only on Save: a BACKGROUND refetch
  // (another staff session changed the default; the row was removed) would
  // otherwise leave A's draft on screen under B's locked "@domain" suffix until
  // the user hit Save and got a silent close (Astra review). Tell them why.
  // The company this render's mismatch check belongs to. Kept in step on EVERY
  // company change (not only mismatched ones — Bugbot), so after a normal
  // switch the next real swap/remove on the new company is still caught.
  const companyRef = useRef(companyId);
  const isCompanySwitchRender = companyRef.current !== companyId;
  companyRef.current = companyId;
  useEffect(() => {
    if (!editingRecordMismatched) return;
    // A company switch also closes the editor (reset effect above) — that is
    // navigation, not a vanished domain, so no warning (Bugbot).
    if (isCompanySwitchRender) return;
    setEditingSender(false);
    setEditingDomainId(null);
    senderForm.resetFields();
    message.warning(
      'The domain you were editing changed or was removed — your unsaved sender changes were discarded.',
    );
  }, [editingRecordMismatched, senderForm, isCompanySwitchRender]);

  if (!canRead || !enabled) return null;

  const run = (
    kind: NonNullable<typeof busy>,
    args: Parameters<typeof mutation.mutate>[0],
    onOk: (response?: { data?: unknown }) => Promise<void> | void,
    fallback: string,
  ) => {
    // In-flight guard: a second click while a mutation is running is ignored,
    // so a double-submit cannot mint a second DNS pack.
    if (busy) return;
    setActionError(null);
    setBusy(kind);
    const startedFor = companyId;
    inFlightCompanyRef.current = startedFor;
    // A callback for a company we have since navigated away from must be
    // dropped entirely — otherwise a completing remove would set
    // removedLocally and hide the NEXT company's existing record.
    const isStale = () => inFlightCompanyRef.current !== startedFor;
    mutation.mutate(args, {
      onSuccess: async (response) => {
        if (isStale()) return;
        // Stay busy until the refetch has landed so the form does not remain
        // submittable on the stale (404) data between success and refetch.
        try {
          await onOk(response);
        } finally {
          if (!isStale()) {
            setBusy(null);
            setBusyRowId(null);
          }
          if (inFlightCompanyRef.current === startedFor) inFlightCompanyRef.current = null;
        }
      },
      onError: (error) => {
        if (isStale()) return;
        inFlightCompanyRef.current = null;
        setBusy(null);
        setBusyRowId(null);
        setActionError(sendingDomainErrorMessage(error, fallback));
      },
    });
  };

  const onCreate = (values: CreateDomainFormValues) =>
    run(
      'create',
      createSendingDomainArgs(companyId, createBodyFromFormValues(values)),
      async (response) => {
        message.success('Sending domain created — DNS records are ready to send to the customer');
        form.resetFields();
        // An editor open on some OTHER row must not remount on the new row
        // holding its draft (Bugbot).
        setEditingSender(false);
        setEditingDomainId(null);
        senderForm.resetFields();
        setTestOutcomeFor(null);
        // Refetch FIRST, then switch: selecting the new id before the list
        // contains it made `record` fall back to an existing domain for a
        // frame, flashing the wrong DNS pack (Bugbot).
        await query.refetch();
        const created = (response?.data as SendingDomain | undefined)?._id;
        if (created) setSelectedId(created);
        setAdding(false);
      },
      'Could not create the sending domain.',
    );

  const onRecheck = () => {
    if (!record?._id) return;
    run(
      'recheck',
      recheckSendingDomainArgs(companyId, record._id),
      async () => {
        message.success('DNS re-checked');
        await query.refetch();
      },
      'Could not re-check DNS.',
    );
  };

  const onMakeDefault = (domainId: string) => {
    setBusyRowId(domainId);
    run(
      'default',
      setDefaultSendingDomainArgs(companyId, domainId),
      async () => {
        message.success('Default domain updated');
        await query.refetch();
      },
      'Could not change the default domain.',
    );
  };

  const onTestSend = () => {
    if (!record?._id) return;
    const testedDomainId = record._id;
    run(
      'test',
      testSendArgs(companyId, testTo.trim(), testedDomainId),
      (response) => {
        // A response for domain A landing after the user switched to B must
        // not render inside B's test panel (Astra review). `selectedId` may be
        // null when the default row is shown implicitly, so compare against
        // the record actually displayed via a ref-free re-derivation.
        // Render what the API says happened — not what was typed. A 200 can
        // still mean "not sent" (SEND_MAIL off) or a redirected recipient.
        const outcome = describeTestSend(parseTestSendResult(response?.data));
        setTestOutcomeFor({ domainId: testedDomainId, outcome });
        if (outcome.type === 'success') message.success(outcome.title);
        else message.warning(outcome.title);
      },
      'Could not send the test email.',
    );
  };

  const startSenderEdit = () => {
    if (!record) return;
    senderForm.setFieldsValue({
      from_local: splitMailbox(record.from_email).local,
      from_name: record.from_name,
      reply_to: record.reply_to ?? '',
    });
    setEditingDomainId(record._id ?? null);
    // Pin the selection to this row. With selection still implicit (null) the
    // panel follows is_default, so "Make default" on another row would swap
    // the record under the open editor and the mismatch guard would discard a
    // draft for a domain that is still there (Bugbot).
    if (record._id) setSelectedId(record._id);
    setEditingSender(true);
  };

  const onSaveSender = (values: { from_local: string; from_name: string; reply_to: string }) => {
    if (!editingDomainId || editingRecordMismatched) {
      setEditingSender(false);
      setEditingDomainId(null);
      return;
    }
    const domain = record?.domain ?? '';
    const body: UpdateSenderBody = {
      from_email: `${values.from_local.trim()}@${domain}`,
      from_name: values.from_name.trim(),
      reply_to: values.reply_to.trim(),
    };
    run(
      'sender',
      updateSenderArgs(companyId, editingDomainId, body),
      async () => {
        message.success('Sender updated — applies to the next email sent from this domain');
        setEditingSender(false);
        setEditingDomainId(null);
        await query.refetch();
      },
      'Could not update the sender.',
    );
  };

  const onRemove = (domainId: string) => {
    setBusyRowId(domainId);
    run(
      'remove',
      removeSendingDomainArgs(companyId, domainId),
      async () => {
        message.success('Sending domain removed');
        if (selectedId === domainId) setSelectedId(null);
        // Single-domain leftover: only clear the create form when it is NOT
        // open. In "Add another" mode a remove on some other row must not wipe
        // the half-typed new domain (Bugbot).
        if (!adding) form.resetFields();
        // Only an editor bound to the REMOVED domain is stale — removing a
        // different row must not wipe an in-progress draft on the selected one
        // (Bugbot). A re-added domain must still never mount in edit mode.
        if (editingDomainId === domainId) {
          setEditingSender(false);
          setEditingDomainId(null);
          senderForm.resetFields();
        }
        await query.refetch();
      },
      'Could not remove the sending domain.',
    );
  };

  const columns = [
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      render: (p: DnsRecord['purpose']) => PURPOSE_LABEL[p] ?? p,
    },
    { title: 'Type', dataIndex: 'kind', key: 'kind', width: 80 },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      render: (h: string) => (
        <Space>
          <Text code>{h}</Text>
          <Tooltip title="Copy host">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              aria-label={`Copy host ${h}`}
              onClick={() => void copyToClipboard(h, 'Host')}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (_v: string, r: DnsRecord) => {
        // Same string on screen, in the per-row copy, and in "Copy all as table".
        const shown = displayValue(r);
        return (
          <Space>
            <Text code>{shown}</Text>
            <Tooltip title="Copy value">
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                aria-label={`Copy value for ${r.host}`}
                onClick={() => void copyToClipboard(shown, 'Value')}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'valid',
      key: 'valid',
      width: 120,
      render: (valid: boolean | null) => <RecordStatusPill valid={valid} />,
    },
  ];

  return (
    <Card
      title="CSAT custom domain"
      style={{ marginTop: 24 }}
      data-testid="csat-custom-domain-section"
    >
      <Paragraph type="secondary">
        Chargeable add-on. A company can hold several verified domains; the one marked default is
        used for links and emails unless a survey overrides it (or the company default is set to the
        standard ServeFirst address above).
      </Paragraph>

      {actionError && (
        <Alert
          type="error"
          showIcon
          message={actionError}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setActionError(null)}
        />
      )}

      {query.isLoading && domains.length === 0 && (
        <Text type="secondary">Loading sending domains…</Text>
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          message="Could not load the sending domains"
          description={sendingDomainErrorMessage(query.error, 'Please try again.')}
        />
      )}

      {domains.length > 0 && (
        <Table<SendingDomain>
          size="small"
          pagination={false}
          rowKey={(d) => d._id ?? d.domain}
          dataSource={domains}
          data-testid="csat-domain-list"
          style={{ marginBottom: 16 }}
          rowClassName={(d) => (record && d._id === record._id ? 'ant-table-row-selected' : '')}
          onRow={(d) => ({
            onClick: () => {
              if (!d._id || d._id === record?._id) return;
              // Switching rows while the sender editor is open: the editor is
              // bound to the OLD domain (editingDomainId) but would render
              // against the new record — a mixed identity. Close it (Bugbot).
              if (editingSender) {
                setEditingSender(false);
                setEditingDomainId(null);
                senderForm.resetFields();
              }
              // The test-send result is scoped to its domain (testOutcomeFor),
              // so no wipe is needed here — it simply stops rendering.
              setSelectedId(d._id);
              setAdding(false);
            },
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Domain',
              key: 'domain',
              render: (_: unknown, d: SendingDomain) => (
                <Space size={4}>
                  <Text code>{d.domain}</Text>
                  {d.is_default ? (
                    <Tag color="blue" data-testid={`csat-domain-default-${d._id}`}>
                      default
                    </Tag>
                  ) : null}
                </Space>
              ),
            },
            {
              title: 'Status',
              key: 'status',
              render: (_: unknown, d: SendingDomain) => <StatusCell record={d} />,
            },
            ...(canWrite
              ? [
                  {
                    title: '',
                    key: 'actions',
                    width: 240,
                    render: (_: unknown, d: SendingDomain) => (
                      <Space onClick={(e) => e.stopPropagation()}>
                        {!d.is_default && d._id ? (
                          <Tooltip title="Use this domain for links and emails unless a survey overrides it">
                            <Button
                              size="small"
                              icon={<StarOutlined />}
                              onClick={() => onMakeDefault(d._id as string)}
                              loading={busy === 'default' && busyRowId === d._id}
                              disabled={busy !== null}
                              aria-label={`Make ${d.domain} the default`}
                            >
                              Make default
                            </Button>
                          </Tooltip>
                        ) : (
                          <Button
                            size="small"
                            icon={<StarFilled />}
                            disabled
                            aria-label={`${d.domain} is the default`}
                          >
                            Default
                          </Button>
                        )}
                        <Popconfirm
                          title={`Remove ${d.domain}?`}
                          description="Re-creating it later will issue NEW DNS records that the customer has to add again."
                          okText="Remove"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => d._id && onRemove(d._id)}
                          disabled={busy !== null}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={busy === 'remove' && busyRowId === d._id}
                            disabled={busy !== null}
                            aria-label={`Remove ${d.domain}`}
                          >
                            Remove
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}

      {domains.length > 0 && canWrite && !adding && (
        <Button
          icon={<PlusOutlined />}
          onClick={() => setAdding(true)}
          disabled={busy !== null}
          style={{ marginBottom: 16 }}
          data-testid="csat-domain-add-another"
        >
          Add another domain
        </Button>
      )}

      {showCreateForm && (
        <Form
          form={form}
          layout="vertical"
          onFinish={onCreate}
          disabled={!canWrite || busy !== null}
          data-testid="csat-domain-create-form"
        >
          <Form.Item
            label="Subdomain"
            name="domain"
            rules={[
              { required: true, message: 'Enter the subdomain agreed with the customer' },
              {
                // Same rule the API applies, so a bad value fails against the field
                // instead of coming back as a 400 after save (SF-4418).
                validator: (_, value) =>
                  !value || isValidSendingHostname(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          'Enter a valid domain, e.g. csat.theircompany.com — letters, numbers and hyphens only, with at least one dot',
                        ),
                      ),
              },
            ]}
            extra="e.g. csat.theircompany.com — a subdomain of their domain, not the root"
          >
            <Input placeholder="csat.theircompany.com" />
          </Form.Item>
          {/*
            Advice, not a rule: a root domain is a valid sending domain and the API
            takes it, but whatever goes in this box is what the customer points DNS
            at — so entering their main domain is worth a second look. Never blocks.
          */}
          {looksLikeRootDomain(createDomainSuffix) && (
            <div
              data-testid="csat-domain-root-warning"
              style={{ marginTop: -16, marginBottom: 16, color: token.colorWarningText }}
            >
              This looks like a root domain. A subdomain such as csat.theircompany.com is
              recommended, because the customer will be pointing DNS at whatever is entered
              here. You can still save it.
            </div>
          )}
          {/* Local part only, with the subdomain pinned as a suffix — same control
              as the edit panel. Typing a full address here used to be possible and
              was then rejected by the API's FROM_DOMAIN_MISMATCH check after Save
              (SF-4417). */}
          <Form.Item
            label="From address"
            name="from_local"
            rules={[
              { required: true, message: 'Enter the part before the @' },
              {
                validator: (_, value: string) =>
                  !value || isValidLocalPart(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Letters, numbers and . _ - + only; no spaces or @'),
                      ),
              },
            ]}
          >
            <Input
              addonAfter={
                <span data-testid="csat-create-domain-suffix">
                  {createDomainSuffix ? `@${createDomainSuffix}` : '@…'}
                </span>
              }
              placeholder="feedback"
              aria-label="From address local part"
            />
          </Form.Item>
          <Form.Item
            label="From name"
            name="from_name"
            rules={[{ required: true, whitespace: true, message: 'Enter the sender name' }]}
          >
            <Input placeholder="Their Company Feedback" />
          </Form.Item>
          <Form.Item
            label="Reply-To"
            name="reply_to"
            rules={[
              {
                // Optional, and validated on the TRIMMED value — matches the edit
                // panel and sf-api's validateCreateInput, which treats an empty
                // reply_to as "none" (SF-4417). AntD's `type: 'email'` is NOT
                // equivalent to the API's rule — see isValidMailbox.
                validator: (_, value: string) =>
                  !value?.trim() || isValidMailbox(value.trim())
                    ? Promise.resolve()
                    : Promise.reject(new Error('Enter a valid email address')),
              },
            ]}
            extra="The subdomain cannot receive mail. Replies go here (their normal support inbox); leave empty for no Reply-To."
          >
            <Input placeholder="support@theircompany.com" />
          </Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={busy === 'create'}
              disabled={!canWrite || (busy !== null && busy !== 'create')}
            >
              Save and generate DNS records
            </Button>
            {adding && (
              <Button
                onClick={() => {
                  setAdding(false);
                  form.resetFields();
                }}
                disabled={busy !== null}
              >
                Cancel
              </Button>
            )}
          </Space>
          {!canWrite && (
            <Text type="secondary" style={{ marginLeft: 12 }}>
              You have read-only access to this section.
            </Text>
          )}
        </Form>
      )}

      {record && !showCreateForm && (
        <>
          {/* Domain + status live in the list above (the row is highlighted); this
              panel is that row's detail — sender identity, DNS pack, actions. */}
          <Descriptions
            column={2}
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <span data-testid="csat-domain-panel-title">
                <Text code>{record.domain}</Text>
              </span>
            }
          >
            {!editingSender && (
              <>
                <Descriptions.Item label="From">
                  <span data-testid="csat-sender-from">
                    {record.from_name} &lt;{record.from_email}&gt;
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Reply-To">
                  <span data-testid="csat-sender-reply-to">{record.reply_to || '—'}</span>
                </Descriptions.Item>
              </>
            )}
          </Descriptions>

          {editingSender ? (
            <Card
              size="small"
              title="Edit sender"
              style={{ marginBottom: 16 }}
              data-testid="csat-sender-edit"
            >
              <Paragraph type="secondary" style={{ marginTop: 0 }}>
                The domain cannot change here — it is what the customer&apos;s DNS records
                authenticate. To move to a different domain, add it as a new domain. Changes apply
                to the next email sent.
              </Paragraph>
              {/* Locked while ANY action is in flight: `run` drops a second
                  mutation silently (if (busy) return), so an enabled Save
                  during a re-check/test-send would look like it did nothing. */}
              <Form
                form={senderForm}
                layout="vertical"
                onFinish={onSaveSender}
                disabled={busy !== null}
              >
                <Form.Item
                  label="From address"
                  name="from_local"
                  rules={[
                    { required: true, message: 'Enter the part before the @' },
                    {
                      validator: (_, value: string) =>
                        !value || isValidLocalPart(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error('Letters, numbers and . _ - + only; no spaces or @'),
                            ),
                    },
                  ]}
                >
                  <Input
                    addonAfter={
                      <span data-testid="csat-sender-domain-suffix">@{record.domain}</span>
                    }
                    placeholder="feedback"
                    aria-label="From address local part"
                  />
                </Form.Item>
                <Form.Item
                  label="From name"
                  name="from_name"
                  rules={[{ required: true, whitespace: true, message: 'Enter the sender name' }]}
                >
                  <Input placeholder="Their Company Feedback" />
                </Form.Item>
                <Form.Item
                  label="Reply-To"
                  name="reply_to"
                  rules={[
                    {
                      // Validate the TRIMMED value: save also trims before sending, and an
                      // empty-after-trim string is the documented "clear Reply-To" payload
                      // (Bugbot — whitespace blocked Save even though it clears cleanly).
                      validator: (_, value: string) =>
                        !value?.trim() || isValidMailbox(value.trim())
                          ? Promise.resolve()
                          : Promise.reject(new Error('Enter a valid email address')),
                    },
                  ]}
                  extra="Leave empty for no Reply-To (replies go to the From address, which cannot receive mail)."
                >
                  <Input placeholder="support@theircompany.com" />
                </Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={busy === 'sender'}
                    disabled={busy !== null}
                  >
                    Save sender
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingSender(false);
                      setEditingDomainId(null);
                      senderForm.resetFields();
                    }}
                    disabled={busy !== null}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form>
            </Card>
          ) : null}

          <Space style={{ marginBottom: 8 }} wrap>
            <Button
              icon={<CopyOutlined />}
              onClick={() => void copyRichToClipboard(dnsText, dnsHtml, 'DNS table')}
            >
              Copy all as table
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={onRecheck}
              loading={busy === 'recheck'}
              disabled={!canWrite || (busy !== null && busy !== 'recheck')}
            >
              Re-check now
            </Button>
            {canWrite && !editingSender && (
              <Button icon={<EditOutlined />} onClick={startSenderEdit} disabled={busy !== null}>
                Edit sender
              </Button>
            )}
            {!canWrite && (
              <Text type="secondary" data-testid="csat-domain-readonly-note">
                Read-only: re-check, edit sender, test-send, make default and remove need
                admin:companies:domain:write.
              </Text>
            )}
          </Space>
          <Table<DnsRecord>
            size="small"
            pagination={false}
            rowKey={(r) => `${r.purpose}-${r.host}`}
            dataSource={record.dns_pack ?? []}
            columns={columns}
            locale={{ emptyText: 'No DNS records yet' }}
          />
          <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
            Common mistakes for the customer&apos;s IT team: Cloudflare must be &quot;DNS only&quot;
            (orange cloud off); do not add a trailing dot twice; the underscore in{' '}
            <Text code>_domainkey</Text>
            {hasDmarcRow ? (
              <>
                {' '}
                (and <Text code>_dmarc</Text>)
              </>
            ) : null}{' '}
            is part of the host name and must be kept.
          </Paragraph>

          <Card size="small" title="Send test email" style={{ marginTop: 16 }}>
            <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
              <Input
                type="email"
                placeholder="you@servefirst.co.uk"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                aria-label="Test email recipient"
                disabled={!canWrite || !canTestSendNow(record, savedEnabled)}
              />
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={onTestSend}
                loading={busy === 'test'}
                disabled={
                  !canWrite ||
                  !canTestSendNow(record, savedEnabled) ||
                  !testTo.trim() ||
                  (busy !== null && busy !== 'test')
                }
              >
                Send test email
              </Button>
            </Space.Compact>
            {!canTestSend(record) && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Available once the domain is at least &quot;Email ready&quot;.
              </Text>
            )}
            {canTestSend(record) && !savedEnabled && (
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: 8 }}
                data-testid="csat-test-send-unsaved-note"
              >
                Save the company to enable test sends.
              </Text>
            )}
            {testOutcome && (
              <Alert
                type={testOutcome.type}
                showIcon
                closable
                onClose={() => setTestOutcomeFor(null)}
                message={testOutcome.title}
                description={testOutcome.detail}
                style={{ marginTop: 12 }}
                data-testid="csat-test-send-result"
              />
            )}
          </Card>
        </>
      )}
    </Card>
  );
};
